import React, { useEffect, useState, createContext, useContext, createElement } from 'react';
import { generateMockData, getMonthName } from '../utils/mockData';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
export interface EnergyReading {
  id: string;
  timestamp: string;
  value: number;
  unit: string;
}
export interface Contact {
  id: string;
  name: string;
  phone: string;
  email: string;
}
interface DataContextType {
  readings: EnergyReading[];
  contacts: Contact[];
  threshold: number;
  setThreshold: (value: number) => void;
  sendSMS: (message: string) => Promise<void>;
  sendEmail: (subject: string, message: string) => Promise<void>;
  notificationLogs: NotificationLog[];
  exportData: () => void;
  monthlyAverages: {
    month: string;
    average: number;
  }[];
}
export interface NotificationLog {
  id: string;
  timestamp: string;
  type: 'sms' | 'email' | 'auto-alert';
  message: string;
  recipients: number;
}
const DataContext = createContext<DataContextType | undefined>(undefined);
const MOCK_CONTACTS: Contact[] = Array.from({
  length: 10
}).map((_, index) => ({
  id: `contact-${index + 1}`,
  name: `Contact ${index + 1}`,
  phone: `+1${Math.floor(1000000000 + Math.random() * 9000000000)}`,
  email: `contact${index + 1}@example.com`
}));
export const DataProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    isAuthenticated
  } = useAuth();
  const [readings, setReadings] = useState<EnergyReading[]>([]);
  const [contacts] = useState<Contact[]>(MOCK_CONTACTS);
  const [threshold, setThreshold] = useState<number>(50);
  const [notificationLogs, setNotificationLogs] = useState<NotificationLog[]>([]);
  const [monthlyAverages, setMonthlyAverages] = useState<{
    month: string;
    average: number;
  }[]>([]);
  useEffect(() => {
    // Load data from localStorage if available
    if (isAuthenticated) {
      const savedReadings = localStorage.getItem('energyReadings');
      const savedThreshold = localStorage.getItem('energyThreshold');
      const savedLogs = localStorage.getItem('notificationLogs');
      if (savedReadings) {
        setReadings(JSON.parse(savedReadings));
      } else {
        const mockData = generateMockData();
        setReadings(mockData);
        localStorage.setItem('energyReadings', JSON.stringify(mockData));
      }
      if (savedThreshold) {
        setThreshold(Number(savedThreshold));
      } else {
        localStorage.setItem('energyThreshold', threshold.toString());
      }
      if (savedLogs) {
        setNotificationLogs(JSON.parse(savedLogs));
      }
    }
  }, [isAuthenticated]);
  useEffect(() => {
    if (readings.length > 0) {
      // Calculate monthly averages for the past 12 months
      const now = new Date();
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(now.getMonth() - 11);
      const monthlyData: Record<string, number[]> = {};
      // Initialize all 12 months
      for (let i = 0; i < 12; i++) {
        const monthDate = new Date(now);
        monthDate.setMonth(now.getMonth() - i);
        const monthKey = `${monthDate.getFullYear()}-${monthDate.getMonth() + 1}`;
        monthlyData[monthKey] = [];
      }
      // Populate with readings
      readings.forEach(reading => {
        const date = new Date(reading.timestamp);
        if (date >= twelveMonthsAgo) {
          const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
          if (monthlyData[monthKey]) {
            monthlyData[monthKey].push(reading.value);
          }
        }
      });
      // Calculate averages
      const averages = Object.entries(monthlyData).map(([key, values]) => {
        const [year, month] = key.split('-').map(Number);
        const average = values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
        return {
          month: `${getMonthName(month - 1)} ${year}`,
          average: Math.round(average * 100) / 100
        };
      });
      // Sort by date (oldest to newest)
      averages.sort((a, b) => {
        const [monthA, yearA] = a.month.split(' ');
        const [monthB, yearB] = b.month.split(' ');
        if (yearA !== yearB) return Number(yearA) - Number(yearB);
        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
      });
      setMonthlyAverages(averages);
    }
  }, [readings]);
  useEffect(() => {
    // Check for readings below threshold and send alerts if needed
    if (readings.length > 0) {
      const latestReading = readings[readings.length - 1];
      if (latestReading.value < threshold) {
        // Only alert if this is a new reading (within last minute)
        const readingTime = new Date(latestReading.timestamp).getTime();
        const currentTime = new Date().getTime();
        const isRecent = currentTime - readingTime < 60000; // within last minute
        if (isRecent) {
          handleAutoAlert(latestReading.value);
        }
      }
    }
  }, [readings, threshold]);
  const handleAutoAlert = async (value: number) => {
    const message = `ALERT: Energy reading has dropped to ${value}kWh, which is below the threshold of ${threshold}kWh.`;
    try {
      // Simulate sending SMS to all contacts
      await new Promise(resolve => setTimeout(resolve, 1000));
      const newLog: NotificationLog = {
        id: `log-${Date.now()}`,
        timestamp: new Date().toISOString(),
        type: 'auto-alert',
        message,
        recipients: contacts.length
      };
      const updatedLogs = [...notificationLogs, newLog];
      setNotificationLogs(updatedLogs);
      localStorage.setItem('notificationLogs', JSON.stringify(updatedLogs));
      toast.warning('Automatic alert sent', {
        description: `Energy reading below threshold. SMS sent to ${contacts.length} contacts.`
      });
    } catch (error) {
      toast.error('Failed to send automatic alert');
    }
  };
  const sendSMS = async (message: string) => {
    try {
      // Simulate sending SMS to all contacts
      await new Promise(resolve => setTimeout(resolve, 1000));
      const newLog: NotificationLog = {
        id: `log-${Date.now()}`,
        timestamp: new Date().toISOString(),
        type: 'sms',
        message,
        recipients: contacts.length
      };
      const updatedLogs = [...notificationLogs, newLog];
      setNotificationLogs(updatedLogs);
      localStorage.setItem('notificationLogs', JSON.stringify(updatedLogs));
      toast.success('SMS sent successfully', {
        description: `Message sent to ${contacts.length} contacts`
      });
    } catch (error) {
      toast.error('Failed to send SMS');
      throw error;
    }
  };
  const sendEmail = async (subject: string, message: string) => {
    try {
      // Simulate sending email to all contacts
      await new Promise(resolve => setTimeout(resolve, 1500));
      const newLog: NotificationLog = {
        id: `log-${Date.now()}`,
        timestamp: new Date().toISOString(),
        type: 'email',
        message: `${subject}: ${message}`,
        recipients: contacts.length
      };
      const updatedLogs = [...notificationLogs, newLog];
      setNotificationLogs(updatedLogs);
      localStorage.setItem('notificationLogs', JSON.stringify(updatedLogs));
      toast.success('Email sent successfully', {
        description: `Email sent to ${contacts.length} contacts`
      });
    } catch (error) {
      toast.error('Failed to send email');
      throw error;
    }
  };
  const exportData = () => {
    try {
      // Create CSV content
      const headers = 'Timestamp,Value (kWh)\n';
      const rows = readings.map(r => `${r.timestamp},${r.value}`).join('\n');
      const csvContent = `data:text/csv;charset=utf-8,${headers}${rows}`;
      // Create download link
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `energy_readings_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      // Trigger download
      link.click();
      document.body.removeChild(link);
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };
  return <DataContext.Provider value={{
    readings,
    contacts,
    threshold,
    setThreshold,
    sendSMS,
    sendEmail,
    notificationLogs,
    exportData,
    monthlyAverages
  }} data-id="element-458">
      {children}
    </DataContext.Provider>;
};
export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};